
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { IChatConversation } from './AgentChatbox.vue';
import ChatHistoryCollection, { ChatSession } from '@/collections/chatHistory';
import SessionsCollection from '@/collections/sessions';

@Component
export default class ChatHistory extends Vue {

  public get filteredConversations(): IChatConversation[] {
    if (!this.searchQuery) {
      return this.conversations;
    }

    const query = this.searchQuery.toLowerCase();
    return this.conversations.filter((conversation) =>
      conversation.title.toLowerCase().includes(query) ||
      conversation.lastMessage.toLowerCase().includes(query),
    );
  }
  @Prop({ default: '' })
  public searchQuery!: string;

  @Prop({ default: null })
  public selectedAgentId!: string | null;

  @Prop({ default: '' })
  public activeConversationId!: string;

  public renameDialog: boolean = false;
  public newConversationTitle: string = '';
  public conversationToRename: IChatConversation | null = null;
  public renamingInProgress: boolean = false;
  public loading: boolean = false;
  public error: string = '';
  public showClearAllDialog: boolean = false;
  public clearingAll: boolean = false;
  public showDeleteDialog: boolean = false;
  public conversationToDelete: IChatConversation | null = null;
  public deletingSingle: boolean = false;

  // Local state for conversations
  public conversations: IChatConversation[] = [];

  // Chat history collection instance
  private chatHistoryCollection = new ChatHistoryCollection();

  // Sessions collection instance for delete operations
  private sessionsCollection = new SessionsCollection();

  // Debounced search
  private searchDebounceTimeout: any = null;

  public async mounted() {
    await this.loadHistoryData();
  }

  @Watch('searchQuery')
  public onSearchQueryChanged(newQuery: string) {
    // Debounce search requests to avoid excessive API calls
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }

    this.searchDebounceTimeout = setTimeout(async () => {
      await this.performSearch(newQuery);
    }, 300);
  }

  @Watch('selectedAgentId')
  public onSelectedAgentChanged() {
    this.loadHistoryData();
  }

  /**
   * Load chat history data from Atlas backend
   */
  public async loadHistoryData() {
    try {
      this.loading = true;
      this.error = '';

      const searchRequest = {
        limit: 50, // Load more sessions initially
        agent_id: this.selectedAgentId || undefined,
      };

      const response = await this.chatHistoryCollection.searchHistory(searchRequest);
      this.conversations = this.convertSessionsToConversations(response.sessions);

    } catch (error: any) {
      console.error('Failed to load chat history:', error);
      this.error = 'Failed to load chat history. Please try again.';
      this.conversations = [];
    } finally {
      this.loading = false;
    }
  }

  /**
   * Perform search with backend API
   */
  public async performSearch(query: string) {
    if (!query.trim()) {
      await this.loadHistoryData();
      return;
    }

    try {
      this.loading = true;
      this.error = '';

      const searchRequest = {
        q: query.trim(),
        limit: 20,
        agent_id: this.selectedAgentId || undefined,
      };

      const response = await this.chatHistoryCollection.searchHistory(searchRequest);
      this.conversations = this.convertSessionsToConversations(response.sessions);

    } catch (error: any) {
      console.error('Failed to search chat history:', error);
      this.error = 'Search failed. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  public async selectConversation(conversation: IChatConversation) {
    try {
      // Clear any previous errors and set loading state
      this.error = '';
      this.loading = true;

      // Load full conversation history from backend
      const sessionDetail = await this.chatHistoryCollection.getSessionHistory(conversation.id);
      
      // Convert backend messages to frontend format
      const messages = (sessionDetail.messages || []).map((msg, index) => ({
        id: msg.id || `msg-${index}-${msg.timestamp}`, // Generate ID if missing
        content: this.cleanMessageContent(msg.content, msg.role),
        timestamp: new Date(msg.timestamp * 1000), // Convert Unix timestamp to Date
        isUser: msg.role === 'user',
        agent: msg.role === 'assistant' ? {
          name: sessionDetail.agent_name,
          avatar: 'mdi-robot',
        } : undefined,
      }));

      // Update conversation with loaded messages and agent info
      const updatedConversation = {
        ...conversation,
        messages,
        agentId: sessionDetail.agent_id,
        agentName: sessionDetail.agent_name,
      };
      

      this.$emit('select-conversation', updatedConversation);
    } catch (error: any) {
      console.error('Failed to load conversation details:', error);
      console.error('Conversation ID:', conversation.id);
      console.error('Error details:', error.message);
      this.error = 'Failed to load conversation. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  public confirmDeleteConversation(conversation: IChatConversation) {
    this.conversationToDelete = conversation;
    this.showDeleteDialog = true;
  }

  public async confirmDeleteSingle() {
    if (!this.conversationToDelete) { return; }

    try {
      this.deletingSingle = true;
      this.error = ''; // Clear any previous errors


      // Use the dedicated SessionsCollection for delete operations
      await this.sessionsCollection.deleteSession(this.conversationToDelete.id);


      // Remove from local list immediately for responsive UI
      this.conversations = this.conversations.filter((c) => c.id !== this.conversationToDelete!.id);

      // Emit event to parent components
      this.$emit('delete-conversation', this.conversationToDelete.id);

      // Close dialog and reset state
      this.showDeleteDialog = false;
      this.conversationToDelete = null;

      // Optionally refresh the chat history to ensure consistency
      // Note: This might not be necessary since we're already updating the local list
      // but it ensures the UI stays in sync with the backend
      // await this.loadHistoryData();

    } catch (error: any) {
      console.error('❌ ChatHistory: Failed to delete conversation:', error);

      // Display user-friendly error messages based on the error type
      if (error.message.includes('Session not found')) {
        this.error = 'This conversation has already been deleted.';
        // Remove from local list if it no longer exists on the server
        this.conversations = this.conversations.filter((c) => c.id !== this.conversationToDelete!.id);
        this.showDeleteDialog = false;
        this.conversationToDelete = null;
      } else if (error.message.includes('Permission denied')) {
        this.error = 'You do not have permission to delete this conversation.';
      } else if (error.message.includes('Network Error')) {
        this.error = 'Connection error. Please check your internet connection and try again.';
      } else if (error.message.includes('Authentication required')) {
        this.error = 'Please log in again to delete conversations.';
      } else {
        this.error = 'Failed to delete conversation. Please try again.';
      }
    } finally {
      this.deletingSingle = false;
    }
  }

  // Legacy method for backwards compatibility (if needed elsewhere)
  public async deleteConversation(conversationId: string) {
    const conversation = this.conversations.find((c) => c.id === conversationId);
    if (conversation) {
      this.confirmDeleteConversation(conversation);
    }
  }

  public renameConversation(conversation: IChatConversation) {
    this.conversationToRename = conversation;
    this.newConversationTitle = conversation.title;
    this.error = '';
    this.renameDialog = true;
  }

  public async confirmRename() {
    // Validate title
    const trimmedTitle = this.newConversationTitle.trim();
    if (!this.conversationToRename || !trimmedTitle) {
      return;
    }

    // Additional validation for title length
    if (trimmedTitle.length > 255) {
      this.error = 'Title must be 255 characters or less';
      return;
    }

    const originalTitle = this.conversationToRename.title;
    const sessionId = this.conversationToRename.id;

    try {
      this.renamingInProgress = true;
      this.error = '';

      // Optimistically update the UI
      const conversation = this.conversations.find((c) => c.id === sessionId);
      if (conversation) {
        conversation.title = trimmedTitle;
      }

      // Call the API to update the title
      const updatedSession = await this.chatHistoryCollection.updateSessionTitle(sessionId, trimmedTitle);

      // Update with the actual response from the server
      if (conversation) {
        conversation.title = updatedSession.title;
      }

      // Emit event for parent components
      this.$emit('rename-conversation', {
        id: sessionId,
        title: updatedSession.title,
      });

      // Close the dialog on success
      this.renameDialog = false;
      this.conversationToRename = null;
      this.newConversationTitle = '';

    } catch (error: any) {
      console.error('Failed to rename conversation:', error);

      // Revert the optimistic update on failure
      const conversation = this.conversations.find((c) => c.id === sessionId);
      if (conversation) {
        conversation.title = originalTitle;
      }

      // Show error message but keep dialog open for retry
      this.error = error.message || 'Failed to rename conversation. Please try again.';
    } finally {
      this.renamingInProgress = false;
    }
  }

  /**
   * Refresh history data (call this when new messages are sent)
   */
  public async refreshHistory() {
    // Clear cache to get fresh data
    this.chatHistoryCollection.clearCache();
    await this.loadHistoryData();
  }

  /**
   * Clear all chat history with confirmation
   */
  public async confirmClearAll() {
    try {
      this.clearingAll = true;
      await this.chatHistoryCollection.clearAllHistory();

      // Clear local state
      this.conversations = [];
      this.showClearAllDialog = false;

      this.$emit('clear-all-history');
    } catch (error: any) {
      console.error('Failed to clear all history:', error);
      this.error = 'Failed to clear history. Please try again.';
    } finally {
      this.clearingAll = false;
    }
  }

  public formatTimestamp(date: Date): string {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return date.toLocaleDateString([], {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  }

  public beforeDestroy() {
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
  }

  /**
   * Clean message content for better UX (extract final output from RunResult)
   */
  private cleanMessageContent(content: string, role: string): string {
    if (role === 'assistant' && content.includes('RunResult:')) {
      // Extract content between "Final output (str): " and the next line
      const finalOutputMatch = content.match(/Final output \(str\):\s*([^\n-]+)/);
      if (finalOutputMatch && finalOutputMatch[1]) {
        return finalOutputMatch[1].trim();
      }
      
      // Fallback: try to extract any quoted content that looks like a message
      const quotedMatch = content.match(/"([^"]+)"/);
      if (quotedMatch && quotedMatch[1] && quotedMatch[1].length > 5) {
        return quotedMatch[1];
      }
    }
    
    // If it doesn't look like debug output or is user message, return as-is
    return content;
  }

  /**
   * Convert ChatSession objects to IChatConversation format
   */
  private convertSessionsToConversations(sessions: ChatSession[]): IChatConversation[] {
    return sessions.map((session) => ({
      id: session.session_id,
      title: session.title,
      lastMessage: session.last_message_preview || 'No messages',
      timestamp: new Date(session.updated_at),
      messages: [], // Will be loaded when conversation is selected
    }));
  }
}
